/**
 * Copyright 2019-2026 ForgeRock AS. All Rights Reserved
 *
 * Use of this code requires a commercial software license with ForgeRock AS
 * or with one of its affiliates. All use shall be exclusively subject
 * to such license between the licensee and ForgeRock AS.
 */

import 'core-js/stable';
import 'regenerator-runtime/runtime';
import 'whatwg-fetch';
import { createApp } from 'vue';
import Vue3Sanitize from 'vue-3-sanitize';
import BootstrapVue from 'bootstrap-vue/dist/bootstrap-vue.esm.min';
import { createPinia } from 'pinia';
import Notifications from '@kyvg/vue3-notification';
import { DEPLOYMENT_TYPES } from '@forgerock/platform-shared/src/constants/deploymentConstants';
import { baseSanitizerConfig } from '@forgerock/platform-shared/src/utils/sanitizerConfig';
import ResizableTable from '@forgerock/platform-shared/src/directives/ResizableTable/ResizableTable';
import platformAuth, { routerGuard as platformRouterGuard } from '@/deployment/platform';
import idmAuth, { routerGuard as idmRouterGuard } from '@/deployment/idm';
import router from '@/router';
import store from '@/store';
import i18n from '@/i18n';
import App from '@/App';

const pinia = createPinia();

function loadApp() {
  const app = createApp(App);
  app.use(router);
  app.use(store);
  app.use(pinia);
  app.use(i18n);
  app.use(BootstrapVue);
  app.use(Notifications);
  app.use(Vue3Sanitize, baseSanitizerConfig);
  router.isReady().then(() => app.mount('#appRoot'));

  // Register the resizable-table directive globally
  app.directive('resizable-table', ResizableTable);

  if (window.Cypress) {
    // only available during E2E tests
    window.app = app;
  }
}

const DEPLOYMENT_TYPE = process.env.VUE_APP_DEPLOYMENT_TYPE || DEPLOYMENT_TYPES.PLATFORM;
store.commit('SharedStore/setDeploymentType', DEPLOYMENT_TYPE);

let authFunction;
let routerGuardFunction;

switch (DEPLOYMENT_TYPE) {
  case DEPLOYMENT_TYPES.IDM:
    authFunction = idmAuth;
    routerGuardFunction = idmRouterGuard;
    break;
  case DEPLOYMENT_TYPES.PLATFORM:
  default:
    authFunction = platformAuth;
    routerGuardFunction = platformRouterGuard;
    break;
}

router.beforeEach(async (to, from, next) => routerGuardFunction(to, from, next, pinia));
authFunction(loadApp, pinia);
